import { Input } from "@/lib/components/Input";
import { Switch } from "@/lib/components/Switch";
import { getSceneByAspectRatio } from "@/lib/stores/editor-store/editor-state";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { SceneTransitionDto, StartSceneDto } from "@repo/api/overlays";
import { EndSceneDto } from "@repo/api/overlays";
import { useEffect, useState } from "react";
import ElementsList, {
  MediaUploadDragHover,
  MediaUploadingIndicator,
} from "./ElementsList/ElementsList";
import AddElementBottomBar from "./ElementsList/AddElementBottomBar";
import { useElementMediaDropzone } from "./ElementsList/element-list-hooks";
import { Slider } from "@/lib/components/Slider";
import SliderInputField from "@/lib/components/SliderInputField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import FormRow from "../ImagesTab/FormRow";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogSelectTrigger,
  DialogTitle,
} from "@/lib/components/Dialog";
import { cn } from "@/lib/utils";
import { Button } from "@/lib/components/Button/Button";

type Props = {
  sceneType: "startScene" | "endScene";
};

const sceneToggleLabels = {
  startScene: "Include start scene",
  endScene: "Include end scene",
};

const sceneDescriptions = {
  startScene: "The start scene plays before the animated images.",
  endScene: "The end scene plays after the animated images.",
};

export default function SceneTab({ sceneType }: Props) {
  const scene = useEditorStore((state) =>
    getSceneByAspectRatio(state, sceneType),
  );
  const toggleSceneInComposition = useEditorStore(
    (state) => state.service.toggleSceneInComposition,
  );

  const { getRootProps, isDragActive, getInputProps, open, isUploadingMedia } =
    useElementMediaDropzone(sceneType, scene !== null);

  const seekToScene = useEditorStore((state) => state.service.seekToScene);

  return (
    <div className="relative flex h-full w-full flex-col" {...getRootProps()}>
      <div className="flex-1 overflow-y-auto pt-5 pb-3">
        <div className="flex items-center gap-4 px-6">
          <div className="input-label m-0">{sceneToggleLabels[sceneType]}</div>
          <Switch
            checked={scene !== null}
            onCheckedChange={(newChecked) => {
              toggleSceneInComposition(sceneType, newChecked);

              if (newChecked) {
                seekToScene(sceneType);
              }
            }}
          />
        </div>
        <h2 className="mt-2.5 px-6 text-sm text-text-secondary">
          {sceneDescriptions[sceneType]}
        </h2>
        {scene !== null && (
          <>
            <div className="mt-8 flex gap-8 px-6">
              <SceneDuration sceneType={sceneType} scene={scene} />
              <TransitionDialog sceneType={sceneType} scene={scene} />
            </div>

            <div className="input-label mt-8 px-6">Elements</div>
            <ElementsList sceneType={sceneType} />
          </>
        )}
      </div>
      {scene !== null && (
        <AddElementBottomBar
          getInputProps={getInputProps}
          open={open}
          sceneType={sceneType}
        />
      )}
      {isDragActive && <MediaUploadDragHover />}
      {isUploadingMedia && <MediaUploadingIndicator />}
    </div>
  );
}

type SceneDurationProps = {
  sceneType: "startScene" | "endScene";
  scene: StartSceneDto | EndSceneDto;
};
function SceneDuration({ sceneType, scene }: SceneDurationProps) {
  const seconds = scene.duration.seconds;
  const updateScene = useEditorStore((state) => state.service.updateScene);

  const [value, setValue] = useState(seconds);

  useEffect(() => {
    setValue(seconds);
  }, [seconds]);

  return (
    <div className="w-full">
      <div className="input-label">Duration (s)</div>
      <div className="flex w-full items-center gap-4">
        <Input
          className="w-[164px]"
          value={value}
          containerSize="sm"
          leadingComponent={<span className="text-text-secondary">Sec</span>}
          onValidateInternalValue={(e) => {
            let parsedValue = e.target.value;
            parsedValue = parsedValue.replace(",", ".");

            return (
              parsedValue === "" ||
              /^\d*$/.test(parsedValue) ||
              /^\d+\.\d{0,2}$/.test(parsedValue)
            );
          }}
          onValidate={(e) => {
            let parsedValue = e.target.value;
            parsedValue = parsedValue.replace(",", ".");

            return parsedValue === "" || /^\d+(\.\d{0,2})?$/.test(parsedValue);
          }}
          onChange={(e) => {
            let parsedValue = e.target.value;
            parsedValue = parsedValue.replace(",", ".");

            updateScene(sceneType, (scene) => {
              scene.duration.seconds = Number(parsedValue);
            });
          }}
        />
        <Slider
          value={value}
          onValueChange={setValue}
          onValueCommit={(newValue) => {
            setValue(newValue);
            updateScene(sceneType, (scene) => {
              scene.duration.seconds = Number(newValue.toFixed(1));
            });
          }}
          min={0}
          max={30}
          step={0.1}
        />
      </div>
    </div>
  );
}

const animationOptions: Record<
  "startScene" | "endScene",
  {
    label: string;
    value: SceneTransitionDto["kind"];
  }[]
> = {
  startScene: [
    {
      label: "Fade out",
      value: "FADE_OUT",
    },
    {
      label: "Stacked",
      value: "STACKED",
    },
  ],
  endScene: [
    {
      label: "Fade in",
      value: "FADE_IN",
    },
    {
      label: "Stacked",
      value: "STACKED",
    },
  ],
};

type TransitionDialogProps = {
  sceneType: "startScene" | "endScene";
  scene: StartSceneDto | EndSceneDto;
};

function TransitionDialog({ sceneType, scene }: TransitionDialogProps) {
  const updateScene = useEditorStore((state) => state.service.updateScene);
  const transition = getSceneTransition(sceneType, scene);
  const duration = transition?.duration ?? 0;
  const maxDuration = Math.min(15, scene.duration.seconds);

  const [value, setValue] = useState(duration);

  useEffect(() => {
    setValue(duration);
  }, [duration]);

  const noneLabel =
    sceneType === "startScene" ? "No exit transition" : "No entry transition";

  const handleKindChange = (newValue: string) => {
    updateScene(sceneType, (scene) => {
      const newTransition =
        newValue === "NONE"
          ? null
          : {
              kind: newValue as SceneTransitionDto["kind"],
              direction: "FROM_BOTTOM" as const,
              // WATCH OUT: Raising this value a lot (like to 1.5s) might cause beat sync to fail for the first animated image.
              duration: transition?.duration ?? 0.7,
              easing: "EASE_IN_OUT" as const,
            };

      if (sceneType === "startScene") {
        (scene as StartSceneDto).exitTransition = newTransition;
      } else {
        (scene as EndSceneDto).entryTransition = newTransition;
      }
    });
  };

  const handleDurationCommit = (newValue: number) => {
    const seconds = Number(newValue.toFixed(1));
    setValue(seconds);
    updateScene(sceneType, (scene) => {
      getSceneTransition(sceneType, scene)!.duration = seconds;
    });
  };

  const title =
    sceneType === "startScene" ? "Exit transition" : "Entry transition";
  const transitionLabel = animationOptions[sceneType].find(
    (option) => option.value === transition?.kind,
  )?.label;

  return (
    <Dialog>
      <div className="w-full">
        <div className="input-label">{title}</div>
        <DialogSelectTrigger
          className={cn("h-9", !transition && "text-text-secondary")}
        >
          {transition
            ? `${transitionLabel} (${transition.duration}s)`
            : noneLabel}
        </DialogSelectTrigger>
      </div>

      <DialogContent
        className="max-w-md"
        classNameInner="gap-0 p-0 sm:px-0"
        showCloseButton={false}
      >
        <DialogHeader variant="bordered" showCloseButton>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2 px-7 py-5">
          <FormRow label="Transition" classNameLabel="w-[130px]">
            <Select
              value={transition?.kind ?? "NONE"}
              onValueChange={handleKindChange}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">{noneLabel}</SelectItem>
                {animationOptions[sceneType].map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormRow>
          {transition && (
            <FormRow label="Duration" classNameLabel="w-[130px]">
              <Slider
                value={value}
                onValueChange={setValue}
                onValueCommit={handleDurationCommit}
                min={0}
                max={maxDuration}
                step={0.1}
              />
              <SliderInputField
                value={value}
                onCommit={handleDurationCommit}
                min={0}
                max={maxDuration}
                className="max-w-12 min-w-12"
                suffix="s"
              />
            </FormRow>
          )}
        </div>
        <div className="flex shrink-0 justify-end gap-3 border-t border-border px-7 py-4">
          <DialogClose asChild>
            <Button size="sm">Done</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getSceneTransition(
  sceneType: "startScene" | "endScene",
  scene: StartSceneDto | EndSceneDto,
) {
  return sceneType === "startScene"
    ? (scene as StartSceneDto).exitTransition
    : (scene as EndSceneDto).entryTransition;
}
